"use client";

import React from "react";
import {
    Phone,
    ChatCircleDots,
    ChartLineUp,
    Laptop,
    Buildings,
    GraduationCap,
    CurrencyDollar,
    Shield,
    Globe,
    Clock,
    Users
} from '@/app/components/Icons/Phosphoricons';
import GetStartedButton from "@/app/components/Utils/GetStartedButton";

const connectFeatures = [
    {
        title: 'Omnichannel Engagement',
        description: 'Seamlessly manage interactions across voice, chat, SMS, and email from a single interface with unified customer history and context for personalized service delivery',
        icon: Phone,
        color: 'bg-blue-600',
    },
    {
        title: 'AI Powered Conversations',
        description: 'Leverage natural language understanding for sophisticated self service options that can handle complex customer inquiries, reducing agent workload while maintaining high satisfaction ratings',
        icon: ChatCircleDots,
        color: 'bg-blue-600',
    },
    {
        title: 'Advanced Analytics',
        description: 'Gain comprehensive insights through real time dashboards and historical reporting with custom metrics, trend analysis, and predictive forecasting to optimize your customer experience strategy',
        icon: ChartLineUp,
        color: 'bg-blue-600',
    },
    {
        title: 'Flexibility & Scalability',
        description: 'Scale from 10 to 10,000+ agents with no infrastructure maintenance, supporting seasonal peaks and business growth with on demand capacity and pay as you go pricing',
        icon: Laptop,
        color: 'bg-blue-600',
    },
    {
        title: 'Global Infrastructure',
        description: 'Deliver consistent customer experiences worldwide with globally distributed contact centers that provide low latency connections and regional compliance capabilities',
        icon: Globe,
        color: 'bg-blue-600',
    },
    {
        title: 'Intelligent Routing',
        description: 'Direct customers to the best suited agent based on skills, availability, and customer history using machine learning algorithms that continuously improve match quality',
        icon: Users,
        color: 'bg-blue-600',
    },
]

const businessBenefits = [
    {
        title: 'Reduce Operational Costs',
        description: 'Save significantly compared to traditional contact center solutions with no upfront hardware investment, predictable usage based pricing, and reduced IT maintenance requirements',
        icon: CurrencyDollar,
    },
    {
        title: 'Improve Agent Productivity',
        description: 'Increase efficiency with intelligent routing, contextual customer information, automated workflows, and AI assisted responses that substantially reduce handling times',
        icon: GraduationCap,
    },
    {
        title: 'Enhance Customer Experience',
        description: 'Provide consistent omnichannel experiences with personalized interactions based on customer history, preferences, and behavior patterns that drive loyalty and satisfaction',
        icon: ChatCircleDots,
    },
    {
        title: 'Enterprise Ready Solution',
        description: 'Deploy a fully managed service trusted by thousands of organizations from startups to Fortune 500 companies with enterprise grade security, reliability, and compliance features',
        icon: Buildings,
    },
    {
        title: 'Rapid Deployment',
        description: 'Implement new contact center capabilities in days rather than months with prebuilt components, intuitive configuration tools, and comprehensive implementation support',
        icon: Clock,
    },
    {
        title: 'Advanced Security & Compliance',
        description: 'Maintain the highest standards of data protection with end to end encryption, comprehensive access controls, and compliance certifications for HIPAA, PCI, GDPR, and other regulations',
        icon: Shield,
    },
]

const industryUseCase = [
    {
        industry: 'Financial Services',
        benefit: 'Secure authentication and personally identifiable information handling with comprehensive compliance controls, fraud detection, and secure payment processing capabilities',
    },
    {
        industry: 'Healthcare',
        benefit: 'HIPAA compliant patient communication with appointment management, secure messaging, medical record access, and integration with electronic health record systems',
    },
    {
        industry: 'Retail & Ecommerce',
        benefit: 'Seasonal scaling with personalized shopping assistance, order management, product recommendations, and seamless integration with inventory and fulfillment systems',
    },
    {
        industry: 'Hospitality & Travel',
        benefit: '24/7 reservation support with global language capabilities, itinerary management, real time updates, and integration with property management and booking systems',
    },
    {
        industry: 'Government & Public Sector',
        benefit: 'Citizen service enhancements with accessible communication channels, secure identity verification, and efficient case management for public service delivery',
    },
    {
        industry: 'Technology & Software',
        benefit: 'Technical support optimization with screen sharing, knowledge base integration, and intelligent routing to specialized support teams based on product and issue type',
    },
]

export default function AWSConnectFeatureBTS() {
    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative isolate overflow-hidden pt-14">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h1 className="text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl font-system">
                            Transform Your Customer Experience with AWS Connect
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 font-system">
                            A cloud based contact center that delivers exceptional customer service at any scale with no infrastructure to manage and pay as you go pricing.
                        </p>
                        <div className="mt-10 flex items-center justify-center gap-x-6">
                            <GetStartedButton />
                        </div>
                    </div>
                </div>
            </div>

            {/* Core Features Section */}
            <div id="learn-more" className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8">
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl font-system">
                        Complete Contact Center Solution
                    </h2>
                    <p className="mx-auto mt-6 max-w-xl text-lg text-gray-600 font-system">
                        AWS Connect provides everything you need to deliver exceptional customer service while reducing operational costs and complexity through an integrated cloud platform.
                    </p>
                </div>

                <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {connectFeatures.map((feature) => (
                        <div key={feature.title} className="relative border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                            <div className={`flex h-14 w-14 items-center justify-center rounded-lg ${feature.color} text-white`}>
                                <feature.icon className="h-8 w-8" weight="bold" aria-hidden="true" />
                            </div>
                            <h3 className="mt-4 text-lg font-semibold text-gray-900 font-system">{feature.title}</h3>
                            <p className="mt-2 text-sm text-gray-600 font-system">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Business Impact Section */}
            <div className="bg-gray-50 py-16 sm:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl font-system">
                            Measurable Business Impact
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-lg text-gray-600 font-system">
                            Our clients achieve significant performance improvements across key business metrics, with ROI typically realized within the first few months of implementation.
                        </p>
                    </div>

                    <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {businessBenefits.map((benefit) => (
                            <div key={benefit.title} className="flex gap-6">
                                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-blue-600 text-white">
                                    <benefit.icon className="h-6 w-6" weight="bold" aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 font-system">{benefit.title}</h3>
                                    <p className="mt-2 text-sm text-gray-600 font-system">{benefit.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Industry Use Cases */}
            <div className="bg-white py-16 sm:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl font-system">
                            Industry Specific Solutions
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-lg text-gray-600 font-system">
                            Tailored contact center capabilities designed to address the unique requirements and challenges of various industries.
                        </p>
                    </div>

                    <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {industryUseCase.map((useCase) => (
                            <div key={useCase.industry} className="relative border border-gray-200 rounded-lg p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 font-system">{useCase.industry}</h3>
                                <p className="mt-2 text-sm text-gray-600 font-system">{useCase.benefit}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Implementation Journey */}
            <div className="bg-gray-50 py-16 sm:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl font-system">
                            Seamless Implementation Journey
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-lg text-gray-600 font-system">
                            Our structured approach ensures a smooth transition to your new cloud contact center with minimal disruption to your business operations.
                        </p>
                    </div>

                    <div className="mx-auto mt-16 max-w-4xl">
                        <ol className="relative border-l border-gray-300">
                            <li className="mb-10 ml-6">
                                <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full -left-4">
                                    <span className="text-white font-bold">1</span>
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900 font-system">Discovery & Assessment</h3>
                                <p className="mt-2 text-base text-gray-600 font-system">
                                    We analyze your current contact center operations, identify pain points, and define clear objectives for your migration to ensure alignment with business goals.
                                </p>
                            </li>
                            <li className="mb-10 ml-6">
                                <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full -left-4">
                                    <span className="text-white font-bold">2</span>
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900 font-system">Solution Design</h3>
                                <p className="mt-2 text-base text-gray-600 font-system">
                                    Our architects create a tailored implementation plan including call flows, integration points, user roles, and reporting requirements specific to your business needs.
                                </p>
                            </li>
                            <li className="mb-10 ml-6">
                                <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full -left-4">
                                    <span className="text-white font-bold">3</span>
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900 font-system">Implementation & Integration</h3>
                                <p className="mt-2 text-base text-gray-600 font-system">
                                    We configure your contact center environment, integrate with existing systems, and develop custom components to deliver a cohesive solution that enhances agent productivity.
                                </p>
                            </li>
                            <li className="mb-10 ml-6">
                                <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full -left-4">
                                    <span className="text-white font-bold">4</span>
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900 font-system">Testing & Training</h3>
                                <p className="mt-2 text-base text-gray-600 font-system">
                                    Comprehensive testing ensures all components work flawlessly while customized training programs prepare your team to leverage the full capabilities of your new solution.
                                </p>
                            </li>
                            <li className="ml-6">
                                <span className="absolute flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full -left-4">
                                    <span className="text-white font-bold">5</span>
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900 font-system">Launch & Continuous Optimization</h3>
                                <p className="mt-2 text-base text-gray-600 font-system">
                                    After successful deployment, we provide ongoing support and optimization services to ensure your contact center continues to evolve with your business requirements.
                                </p>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-blue-100">
                <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8 lg:flex lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl font-system">
                            Ready to revolutionize your customer service?
                        </h2>
                        <p className="mt-4 text-lg text-gray-600 font-system">
                            Join thousands of organizations that have transformed their customer experience with our cloud contact center solution. Our team of experts is ready to guide you through every step of your journey.
                        </p>
                    </div>
                    <div className="mt-8 flex gap-x-6 lg:mt-0 lg:shrink-0">
                        <GetStartedButton classOverride="bg-white text-blue-600 hover:bg-gray-100" />
                    </div>
                </div>
            </div>
        </div>
    );
}